import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Diamond, Folder, Layers, Plus, X } from 'lucide-react';
import type { AssigneeDoc, WbsItemDoc } from '@mct/shared';
import { cn } from '../../lib/utils';
import {
  buildSuccessorCandidates,
  filterCandidates,
  statusDotClass,
  type WbsDepCandidate,
} from '../../lib/wbsDependencyPicker';

/**
 * Editable successor (downstream) editor — symmetric counterpart to
 * {@link DependencyChips}. Each row of the picker lists tasks that *can*
 * be made to depend on the current item; selecting one writes
 * `item.itemId` into the candidate's `dependsOn` array. The chip ✕ on an
 * existing successor mirrors the operation in reverse: it strips
 * `item.itemId` out of that successor's `dependsOn`.
 *
 * The asymmetric mutation (we write to *another* document, not to
 * `item`) is the reason callers thread two callbacks rather than one
 * `onChange(next)` — there's no single doc whose `dependsOn` represents
 * the successor side.
 */

/**
 * Pure helper. Returns every item in `allItems` whose `dependsOn`
 * contains `itemId`, sorted by `sortKey` (ja-locale, name as tiebreak)
 * to match the order in `buildDependencyCandidates`.
 *
 * Exported so the WBS panel and the unit tests share a single source
 * of truth — there's no clever reverse-index here, just an O(N × M)
 * scan that's plenty for the few-hundred-rows scale this UI handles.
 */
export function findSuccessors(
  itemId: string,
  allItems: readonly WbsItemDoc[],
): WbsItemDoc[] {
  const out: WbsItemDoc[] = [];
  for (const i of allItems) {
    if (i.itemId === itemId) continue;
    if (i.dependsOn.includes(itemId)) out.push(i);
  }
  out.sort((a, b) => {
    const k = a.sortKey.localeCompare(b.sortKey, 'ja');
    if (k !== 0) return k;
    return a.name.localeCompare(b.name, 'ja');
  });
  return out;
}

function KindIcon({ kind }: { kind: WbsItemDoc['kind'] }) {
  if (kind === 'phase') {
    return <Folder className="h-3 w-3 shrink-0 text-blue-600" aria-hidden />;
  }
  if (kind === 'milestone') {
    return <Diamond className="h-3 w-3 shrink-0 text-amber-600" aria-hidden />;
  }
  return <Layers className="h-3 w-3 shrink-0 text-slate-500" aria-hidden />;
}

export function SuccessorChips({
  item,
  allItems,
  assignees = [],
  onAddSuccessor,
  onRemoveSuccessor,
  onSelectItem,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  /** Same source as predecessor picker — used to label assignees. */
  assignees?: readonly AssigneeDoc[];
  /**
   * Add the current `item.itemId` to `successorItemId`'s `dependsOn`.
   * The caller owns the mutation (Firestore write) — this component
   * just decides *which* document to flip.
   */
  onAddSuccessor: (successorItemId: string) => void | Promise<void>;
  /** Remove `item.itemId` from `successorItemId`'s `dependsOn`. */
  onRemoveSuccessor: (successorItemId: string) => void | Promise<void>;
  onSelectItem?: (itemId: string) => void;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const addBtnRef = useRef<HTMLButtonElement | null>(null);

  const successors = useMemo(
    () => findSuccessors(item.itemId, allItems),
    [item.itemId, allItems],
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        {successors.length === 0 ? (
          <span className="text-[11px] text-slate-400">後続タスクなし</span>
        ) : (
          successors.map((s) => (
            <SuccessorChip
              key={s.itemId}
              item={s}
              onRemove={() => void onRemoveSuccessor(s.itemId)}
              onClick={() => onSelectItem?.(s.itemId)}
            />
          ))
        )}
        <button
          ref={addBtnRef}
          type="button"
          onClick={() => setPopoverOpen((v) => !v)}
          className={cn(
            'inline-flex items-center gap-0.5 rounded-md border border-dashed border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-600 hover:border-slate-400 hover:bg-slate-50',
            popoverOpen ? 'border-sky-400 bg-sky-50 text-sky-700' : '',
          )}
          aria-haspopup="listbox"
          aria-expanded={popoverOpen}
          aria-label="後続タスクを追加"
        >
          <Plus className="h-3 w-3" />
          追加
        </button>
      </div>
      {popoverOpen ? (
        <SuccessorPickerPopover
          item={item}
          allItems={allItems}
          assignees={assignees}
          anchor={addBtnRef.current}
          onClose={() => setPopoverOpen(false)}
          onPick={(id) => {
            void onAddSuccessor(id);
            setPopoverOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function SuccessorChip({
  item,
  onRemove,
  onClick,
}: {
  item: WbsItemDoc;
  onRemove: () => void;
  onClick: () => void;
}) {
  return (
    <span
      className="group inline-flex max-w-[220px] items-center gap-1 rounded-full border border-slate-200 bg-slate-50 py-0.5 pl-1.5 pr-0.5 text-[11px] text-slate-700 shadow-sm hover:border-slate-300 hover:bg-white"
      data-succ-chip={item.itemId}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 items-center gap-1 outline-none focus-visible:ring-1 focus-visible:ring-sky-400 rounded-full"
        title={item.name}
      >
        <KindIcon kind={item.kind} />
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
            statusDotClass(item.status),
          )}
          aria-hidden
        />
        {/* sortKey prefix intentionally absent (see DependencyChip). */}
        <span className="truncate">{item.name}</span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`${item.name} を後続から外す`}
        className="ml-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-red-600"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

interface PopoverPlacement {
  top: number;
  left: number;
  width: number;
}

function SuccessorPickerPopover({
  item,
  allItems,
  assignees,
  anchor,
  onClose,
  onPick,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  assignees: readonly AssigneeDoc[];
  anchor: HTMLElement | null;
  onClose: () => void;
  onPick: (id: string) => void;
}) {
  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState<PopoverPlacement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(rawQuery), 120);
    return () => window.clearTimeout(t);
  }, [rawQuery]);

  const baseCandidates = useMemo(
    () => buildSuccessorCandidates(item, allItems, assignees),
    [item, allItems, assignees],
  );

  const visible = useMemo(
    () => filterCandidates(baseCandidates, debouncedQuery),
    [baseCandidates, debouncedQuery],
  );

  useEffect(() => {
    setActiveIndex((prev) => {
      if (visible.length === 0) return 0;
      return Math.min(prev, visible.length - 1);
    });
  }, [visible]);

  useLayoutEffect(() => {
    if (!anchor) return;
    const update = () => {
      const r = anchor.getBoundingClientRect();
      setPlacement({
        top: r.bottom + 4,
        left: r.left,
        width: 320,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchor]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (popoverRef.current?.contains(target)) return;
      if (anchor?.contains(target ?? null)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLLIElement>(
      `[data-cand-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!placement) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) =>
        visible.length === 0 ? 0 : (i + 1) % visible.length,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) =>
        visible.length === 0 ? 0 : (i - 1 + visible.length) % visible.length,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = visible[activeIndex];
      if (pick) onPick(pick.itemId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const popover = (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="後続タスクを検索して追加"
      className="z-50 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
      style={{
        position: 'fixed',
        top: placement.top,
        left: placement.left,
        width: placement.width,
      }}
      onKeyDown={onKeyDown}
    >
      <div className="border-b border-slate-200 p-2">
        <input
          autoFocus
          type="text"
          placeholder="タスクを検索 (キー / 名称 / 担当)"
          value={rawQuery}
          onChange={(e) => setRawQuery(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-200"
          aria-controls="succ-picker-list"
          aria-activedescendant={
            visible[activeIndex]
              ? `succ-cand-${visible[activeIndex]!.itemId}`
              : undefined
          }
        />
      </div>
      <ul
        ref={listRef}
        id="succ-picker-list"
        role="listbox"
        className="max-h-64 overflow-auto py-1"
      >
        {visible.length === 0 ? (
          <li className="px-3 py-4 text-center text-xs text-slate-500">
            該当するタスクがありません
          </li>
        ) : (
          visible.map((c, idx) => (
            <SuccessorCandidateRow
              key={c.itemId}
              candidate={c}
              active={idx === activeIndex}
              index={idx}
              onHover={() => setActiveIndex(idx)}
              onPick={() => onPick(c.itemId)}
            />
          ))
        )}
      </ul>
    </div>
  );

  return createPortal(popover, document.body);
}

function SuccessorCandidateRow({
  candidate,
  active,
  index,
  onHover,
  onPick,
}: {
  candidate: WbsDepCandidate;
  active: boolean;
  index: number;
  onHover: () => void;
  onPick: () => void;
}) {
  return (
    <li
      id={`succ-cand-${candidate.itemId}`}
      role="option"
      aria-selected={active}
      data-cand-index={index}
      onMouseMove={onHover}
      onMouseDown={(e) => {
        e.preventDefault();
        onPick();
      }}
      className={cn(
        'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs',
        active ? 'bg-sky-100 text-sky-900' : 'text-slate-700 hover:bg-slate-50',
      )}
    >
      <KindIcon kind={candidate.kind} />
      <span
        className={cn(
          'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
          statusDotClass(candidate.status),
        )}
        aria-hidden
      />
      {/* sortKey prefix intentionally not rendered (see DependencyChips). */}
      <span className="min-w-0 flex-1 truncate">{candidate.name}</span>
      {candidate.assigneeName ? (
        <span className="truncate text-[10px] text-slate-400">
          {candidate.assigneeName}
        </span>
      ) : null}
    </li>
  );
}
